import { useEffect, useState } from 'react';
import { PRODUCT_CATEGORIES } from '@/content/products';
import { Icon } from './Icon';

/**
 * 产品目录页的左侧类目栏。
 *
 * 粘性定位，并用 IntersectionObserver 高亮当前所在的类目 —— 比监听 scroll
 * 便宜，也不会在滚动时抖动。窄屏收成一行可横向滚动的胶囊。
 */
export function CategorySidebar({ onNavigate }: { onNavigate?: (id: string) => void }) {
  const [activeId, setActiveId] = useState<string>(PRODUCT_CATEGORIES[0]?.id ?? '');

  useEffect(() => {
    const sections = PRODUCT_CATEGORIES.map((category) =>
      document.getElementById(`cat-${category.id}`),
    ).filter((element): element is HTMLElement => element !== null);

    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveId(visible.target.id.replace(/^cat-/, ''));
      },
      // 顶部让开吸顶导航，底部收窄，使"当前类目"落在视口上三分之一处。
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <nav aria-label="产品类目" className="lg:sticky lg:top-24">
      <ul className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:gap-1 lg:overflow-visible lg:pb-0">
        {PRODUCT_CATEGORIES.map((category) => {
          const isActive = category.id === activeId;
          return (
            <li key={category.id} className="shrink-0">
              <a
                href={`#cat-${category.id}`}
                onClick={() => onNavigate?.(category.id)}
                aria-current={isActive ? 'true' : undefined}
                className={[
                  'flex items-center gap-2.5 rounded-btn px-3 py-2.5 text-sm whitespace-nowrap transition-colors',
                  isActive
                    ? 'bg-grad-tint font-medium text-brand'
                    : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
                ].join(' ')}
              >
                <Icon name={category.icon} size={16} className={isActive ? 'text-brand' : 'text-fg-subtle'} />
                {category.name}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
